import {
  ENTRY_CONDITIONS,
  EntryCondition,
  HAVE_LIST_STOCK_DEFAULTS,
  HaveEntryDefaults,
  WANT_LIST_STOCK_DEFAULTS,
  WantEntryDefaults,
} from 'constants/entryDefaults';
import { formatLanguage, getLanguageDisplayName, normalizeLanguageCode } from 'constants/languages';
import {
  CardFinish,
  FINISH_LABELS,
  FINISH_ORDER,
  FinishSource,
  finishFields,
  fromHaveFinish,
  predictStoredFinish,
  toHaveFinish,
} from 'utils/finishes';
import { mapBackendConditionsToFrontend, mapFrontendConditionsToBackend } from 'utils/wantListDefaultsMapper';
import { CardCondition } from 'types/interfaces/condition/condition';
import type { AddCardToHaveListRequest } from 'store/services/collectionList/haveList/haveListApi';
import type { HaveListDefaults, WantListDefaults } from 'store/services/settings/settingsApi';

const SEP = ' · ';

const FINISH_SHORT: Record<CardFinish, string> = { nonfoil: 'NF', foil: 'F', etched: 'E' };

const isEntryCondition = (value: string): value is EntryCondition =>
  (ENTRY_CONDITIONS as readonly string[]).includes(value);

// ---------------------------------------------------------------------------
// Server <-> local shapes
// ---------------------------------------------------------------------------

/** Missing fields (a backend that predates them) read as the stock default. */
export const haveEntryDefaultsFromServer = (
  server: Pick<HaveListDefaults, 'defaultEntryCondition' | 'defaultEntryFinish' | 'defaultEntryLanguage'> | undefined,
): HaveEntryDefaults => {
  const condition = server?.defaultEntryCondition
    ? mapBackendConditionsToFrontend([server.defaultEntryCondition])[0]
    : undefined;
  return {
    condition: condition && isEntryCondition(condition) ? condition : HAVE_LIST_STOCK_DEFAULTS.condition,
    finish: server?.defaultEntryFinish
      ? fromHaveFinish(server.defaultEntryFinish)
      : HAVE_LIST_STOCK_DEFAULTS.finish,
    language: server?.defaultEntryLanguage
      ? normalizeLanguageCode(server.defaultEntryLanguage).toUpperCase()
      : HAVE_LIST_STOCK_DEFAULTS.language,
  };
};

export const haveEntryDefaultsToServer = (
  patch: Partial<HaveEntryDefaults>,
): Partial<HaveListDefaults> => {
  const out: Partial<HaveListDefaults> = {};
  if (patch.condition !== undefined) {
    out.defaultEntryCondition = mapFrontendConditionsToBackend([patch.condition])[0] as CardCondition;
  }
  if (patch.finish !== undefined) out.defaultEntryFinish = toHaveFinish(patch.finish);
  // The card-table spelling (zhs / zht), which is what the backend stores and returns.
  if (patch.language !== undefined) out.defaultEntryLanguage = normalizeLanguageCode(patch.language);
  return out;
};

export const wantEntryDefaultsFromServer = (server: WantListDefaults | undefined): WantEntryDefaults => {
  if (!server) return WANT_LIST_STOCK_DEFAULTS;
  const finishes: CardFinish[] = [];
  if (server.defaultAcceptNonFoil) finishes.push('nonfoil');
  if (server.defaultAcceptFoil) finishes.push('foil');
  if (server.defaultAcceptEtched) finishes.push('etched');
  return {
    conditions: server.defaultConditions?.length ? server.defaultConditions : WANT_LIST_STOCK_DEFAULTS.conditions,
    finishes: finishes.length ? finishes : WANT_LIST_STOCK_DEFAULTS.finishes,
    languages: server.defaultLanguages?.length ? server.defaultLanguages : WANT_LIST_STOCK_DEFAULTS.languages,
  };
};

export const wantEntryDefaultsToServer = (patch: Partial<WantEntryDefaults>): Partial<WantListDefaults> => {
  const out: Partial<WantListDefaults> = {};
  if (patch.conditions !== undefined) out.defaultConditions = patch.conditions;
  if (patch.languages !== undefined) out.defaultLanguages = patch.languages;
  if (patch.finishes !== undefined) {
    out.defaultAcceptNonFoil = patch.finishes.includes('nonfoil');
    out.defaultAcceptFoil = patch.finishes.includes('foil');
    out.defaultAcceptEtched = patch.finishes.includes('etched');
  }
  return out;
};

// ---------------------------------------------------------------------------
// Stock comparison
// ---------------------------------------------------------------------------

const sameSet = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((v) => b.includes(v));

export const isHaveStock = (v: HaveEntryDefaults): boolean =>
  v.condition === HAVE_LIST_STOCK_DEFAULTS.condition &&
  v.finish === HAVE_LIST_STOCK_DEFAULTS.finish &&
  v.language === HAVE_LIST_STOCK_DEFAULTS.language;

export const isWantStock = (v: WantEntryDefaults): boolean =>
  sameSet(v.conditions, WANT_LIST_STOCK_DEFAULTS.conditions) &&
  sameSet(v.finishes, WANT_LIST_STOCK_DEFAULTS.finishes) &&
  sameSet(v.languages, WANT_LIST_STOCK_DEFAULTS.languages);

// ---------------------------------------------------------------------------
// Pill summaries
// ---------------------------------------------------------------------------

export interface EntrySummary {
  /** e.g. "LP · Foil · EN" */
  full: string;
  /** Below 640px: finish abbreviated, e.g. "LP · F · EN" */
  short: string;
}

export const summarizeHaveDefaults = (v: HaveEntryDefaults): EntrySummary => {
  const lang = formatLanguage(v.language);
  return {
    full: [v.condition, FINISH_LABELS[v.finish], lang].join(SEP),
    short: [v.condition, FINISH_SHORT[v.finish], lang].join(SEP),
  };
};

/** A range when the conditions are contiguous in NM..HP order, otherwise a list. */
const summarizeConditions = (conditions: readonly string[]): string => {
  const ordered = ENTRY_CONDITIONS.filter((c) => conditions.includes(c));
  if (ordered.length === 0) return conditions.join(', ');
  if (ordered.length === 1) return ordered[0];
  const first = ENTRY_CONDITIONS.indexOf(ordered[0]);
  const last = ENTRY_CONDITIONS.indexOf(ordered[ordered.length - 1]);
  const contiguous = last - first + 1 === ordered.length;
  return contiguous ? `${ordered[0]}–${ordered[ordered.length - 1]}` : ordered.join(', ');
};

const summarizeLanguages = (languages: readonly string[]): string => {
  if (languages.length === 0) return formatLanguage(WANT_LIST_STOCK_DEFAULTS.languages[0]);
  const first = formatLanguage(languages[0]);
  return languages.length > 1 ? `${first} +${languages.length - 1}` : first;
};

export const summarizeWantDefaults = (v: WantEntryDefaults): EntrySummary => {
  const ordered = FINISH_ORDER.filter((f) => v.finishes.includes(f));
  const all = ordered.length === FINISH_ORDER.length;
  const conditions = summarizeConditions(v.conditions);
  const languages = summarizeLanguages(v.languages);
  return {
    full: [conditions, all ? 'Any finish' : ordered.map((f) => FINISH_LABELS[f]).join('/'), languages].join(SEP),
    short: [conditions, all ? 'Any' : ordered.map((f) => FINISH_SHORT[f]).join('/'), languages].join(SEP),
  };
};

// ---------------------------------------------------------------------------
// Have List add: payload and the dropdown's preview line
// ---------------------------------------------------------------------------

/**
 * The language an add carries. The default applies only when the clicked
 * printing is English; a clicked non-English printing is sent as clicked.
 */
export const entryLanguageFor = (
  card: { lang?: string | null } | undefined | null,
  defaults: HaveEntryDefaults,
): string => {
  const clicked = card?.lang ? normalizeLanguageCode(card.lang) : 'en';
  return clicked !== 'en' ? clicked : normalizeLanguageCode(defaults.language);
};

/**
 * The add-card body for a click in the Have List toolbar. `cardId` stays the
 * printing the user clicked: the backend resolves the language printing and
 * corrects a finish the printing does not exist in, reporting both.
 */
export const buildHaveAddPayload = (
  cardId: number,
  card: { lang?: string | null } | undefined | null,
  defaults: HaveEntryDefaults,
): AddCardToHaveListRequest => ({
  cardId,
  quantity: 1,
  condition: mapFrontendConditionsToBackend([defaults.condition])[0] as CardCondition,
  ...finishFields(toHaveFinish(defaults.finish)),
  language: entryLanguageFor(card, defaults),
  source: 'collection',
});

/** "Add as LP · Foil", or "No foil, adds as LP · Non-foil" when the backend will correct it. */
export const describeHaveAdd = (
  card: (FinishSource & { lang?: string | null }) | undefined | null,
  defaults: HaveEntryDefaults,
): string => {
  const stored = predictStoredFinish(card, defaults.finish);
  const parts = [defaults.condition, FINISH_LABELS[stored]];
  const language = entryLanguageFor(card, defaults);
  if (language !== 'en') parts.push(formatLanguage(language));
  const what = parts.join(SEP);
  return stored === defaults.finish
    ? `Add as ${what}`
    : `No ${FINISH_LABELS[defaults.finish].toLowerCase()}, adds as ${what}`;
};

/** "NM · Non-foil · EN": the settings part of the Fast Entry add confirmation. */
export const describeHaveAddSettings = (
  card: (FinishSource & { lang?: string | null }) | undefined | null,
  defaults: HaveEntryDefaults,
): string => {
  const stored = predictStoredFinish(card, defaults.finish);
  const language = entryLanguageFor(card, defaults);
  return [defaults.condition, FINISH_LABELS[stored], language.toUpperCase()].join(SEP);
};

export interface LanguageAdjustment {
  requested: string;
  stored: string;
  reason: string;
}

export const describeLanguageAdjustment = (adj: LanguageAdjustment | null | undefined): string | null => {
  if (!adj) return null;
  return `No ${getLanguageDisplayName(adj.requested)} printing of this card: added as ${getLanguageDisplayName(adj.stored)}.`;
};
